import React, { useEffect, useMemo, useState } from 'react';
import { Card, Typography, Breadcrumb, Tabs, Row, Col, Statistic, Spin, Empty, List, Tag, Space, Alert } from 'antd';
import { HomeOutlined, ClockCircleOutlined, BarChartOutlined, OrderedListOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { Line, Column } from '@ant-design/plots';
import { getMyUsageStats, UsageStats } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { getDemoUsageStats } from '../../demo/demoBrowseMocks';

const { Title, Text } = Typography;

type Period = 'week' | 'month' | 'year';

const formatMinutes = (minutes: number) => {
  if (!minutes) return '0 分钟';
  if (minutes < 60) return `${Math.round(minutes)} 分钟`;
  const hours = minutes / 60;
  if (hours < 10) return `${hours.toFixed(1)} 小时`;
  return `${hours.toFixed(1)} 小时`;
};

const WorkHours: React.FC = () => {
  const { isDemoBrowseOnly } = useAuth();
  const [period, setPeriod] = useState<Period>('week');
  const [stats, setStats] = useState<UsageStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchStats = async (p: Period) => {
    try {
      setLoading(true);
      setError(null);
      if (isDemoBrowseOnly) {
        setStats(getDemoUsageStats(p));
        return;
      }
      const res = await getMyUsageStats({ period: p });
      if (res.success && res.data) {
        setStats(res.data);
      } else {
        setError(res.message || '获取使用时长失败');
      }
    } catch (e: any) {
      setError(e?.message || '获取使用时长失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats(period);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, isDemoBrowseOnly]);

  const rangeText = useMemo(() => {
    if (!stats?.range) return '';
    const start = stats.range.start ? dayjs(stats.range.start).format('YYYY-MM-DD') : '';
    const end = stats.range.end ? dayjs(stats.range.end).format('YYYY-MM-DD') : '';
    return start && end ? `${start} 至 ${end}` : '';
  }, [stats]);

  const lineData = useMemo(
    () =>
      stats?.timeline?.map((item) => ({
        date: item.date,
        hours: Number((item.durationMinutes / 60).toFixed(2)),
        count: item.count,
      })) || [],
    [stats]
  );

  const instrumentData = useMemo(
    () =>
      stats?.instrumentBreakdown?.map((item) => ({
        instrument: item.instrumentName,
        hours: Number((item.durationMinutes / 60).toFixed(2)),
        count: item.count,
      })) || [],
    [stats]
  );

  const hasData = (stats?.totalDurationMinutes || 0) > 0;

  return (
    <div className="page-container full-height">
      {/* 面包屑导航 */}
      <Breadcrumb
        style={{ marginBottom: '16px' }}
        items={[
          { title: (<><HomeOutlined /><span>首页</span></>) },
          { title: '工作台' },
          { title: '工作时长' },
        ]}
      />

      <div className="page-header">
        <h1 className="page-title">工作时长</h1>
        <p className="page-subtitle">查看自己的仪器预约使用情况</p>
      </div>

      <div className="content-area">
        <Card>
          <Tabs
            activeKey={period}
            onChange={(key) => setPeriod(key as Period)}
            items={[
              { key: 'week', label: '本周' },
              { key: 'month', label: '本月' },
              { key: 'year', label: '本年' },
            ]}
          />

          {rangeText && (
            <div style={{ marginBottom: 12, color: '#8c8c8c' }}>统计范围：{rangeText}</div>
          )}

          {error && (
            <Alert
              type="error"
              message="获取数据失败"
              description={error}
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}

          <Spin spinning={loading}>
            {hasData ? (
              <>
                <Row gutter={[16, 16]} style={{ marginBottom: 8 }}>
                  <Col xs={24} sm={12} md={8}>
                    <Card>
                      <Statistic
                        title="总使用时长"
                        value={formatMinutes(stats!.totalDurationMinutes)}
                        prefix={<ClockCircleOutlined style={{ color: '#13c2c2' }} />}
                      />
                    </Card>
                  </Col>
                  <Col xs={24} sm={12} md={8}>
                    <Card>
                      <Statistic
                        title="使用次数"
                        value={stats!.totalCount}
                        prefix={<OrderedListOutlined style={{ color: '#1890ff' }} />}
                      />
                    </Card>
                  </Col>
                  <Col xs={24} sm={12} md={8}>
                    <Card>
                      <Statistic
                        title="平均每次"
                        value={formatMinutes(stats!.averageDurationMinutes)}
                        prefix={<BarChartOutlined style={{ color: '#faad14' }} />}
                      />
                    </Card>
                  </Col>
                </Row>

                <Row gutter={[16, 16]}>
                  <Col xs={24} lg={14}>
                    <Card title="使用时长趋势">
                      {lineData.length ? (
                        <Line
                          height={260}
                          data={lineData}
                          xField="date"
                          yField="hours"
                          smooth
                          point={{ size: 4 }}
                          tooltip={{
                            formatter: (datum: any) => ({
                              name: '小时',
                              value: datum.hours,
                            }),
                          }}
                        />
                      ) : (
                        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无趋势数据" />
                      )}
                    </Card>
                  </Col>
                  <Col xs={24} lg={10}>
                    <Card title="按仪器分布">
                      {instrumentData.length ? (
                        <Column
                          height={260}
                          data={instrumentData}
                          xField="instrument"
                          yField="hours"
                          meta={{
                            instrument: { alias: '仪器' },
                            hours: { alias: '小时' },
                          }}
                          label={{
                            position: 'middle',
                            style: { fill: '#595959' },
                          }}
                          xAxis={{ label: { autoHide: true, autoRotate: true } }}
                        />
                      ) : (
                        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无仪器数据" />
                      )}
                    </Card>
                  </Col>
                </Row>

                <Card title="最近使用记录" style={{ marginTop: 16 }}>
                  <List
                    dataSource={lineData.slice(-10).reverse()}
                    locale={{ emptyText: '暂无记录' }}
                    renderItem={(item) => (
                      <List.Item>
                        <Space direction="vertical" size={4}>
                          <Space size="small">
                            <Text strong>{item.date}</Text>
                            <Tag color="blue">{item.hours} 小时</Tag>
                            <Tag>{item.count} 次</Tag>
                          </Space>
                          {instrumentData.length > 0 && (
                            <Text type="secondary">按上方仪器分布查看详情</Text>
                          )}
                        </Space>
                      </List.Item>
                    )}
                  />
                </Card>
              </>
            ) : (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="暂无使用数据"
                style={{ marginTop: 32, marginBottom: 32 }}
              />
            )}
          </Spin>
        </Card>
      </div>
    </div>
  );
};

export default WorkHours;
